/**
 * Ported from three/examples/jsm/lines/LineSegments2.js (MIT License).
 * Source: https://github.com/mrdoob/three.js/blob/master/examples/jsm/lines/
 *
 * Adapted to TypeScript:
 *   - Strict typing on constructor params and internal state
 *   - GLSL shader extracted to a separate `.glsl.ts` template literal module
 *   - `onUploadCallback` defensive null-ing (Opt 1a) added on instance buffers
 */

import * as THREE from "three";

import { LineMaterial } from "./LineMaterial";
import { LineSegmentsGeometry } from "./LineSegmentsGeometry";

const _viewport = new THREE.Vector4();

const _start = new THREE.Vector3();
const _end = new THREE.Vector3();

const _start4 = new THREE.Vector4();
const _end4 = new THREE.Vector4();

const _ssOrigin = new THREE.Vector4();
const _ssOrigin3 = new THREE.Vector3();
const _mvMatrix = new THREE.Matrix4();
const _line = new THREE.Line3();
const _closestPoint = new THREE.Vector3();

const _box = new THREE.Box3();
const _sphere = new THREE.Sphere();
const _clipToWorldVector = new THREE.Vector4();

// Mutable module-level state mirrors the original JS module — the helper
// `raycast*` functions read these so the per-call hot path doesn't allocate.
let _ray: THREE.Ray;
let _lineWidth: number;

// Returns the margin required to expand by in world space given the distance from the camera,
// line width, resolution, and camera projection
function getWorldSpaceHalfWidth(
  camera: THREE.Camera,
  distance: number,
  resolution: THREE.Vector2,
): number {
  // transform into clip space, adjust the x and y values by the pixel width offset, then
  // transform back into world space to get world offset. Note clip space is [-1, 1] so full
  // width does not need to be halved.
  _clipToWorldVector.set(0, 0, -distance, 1.0).applyMatrix4(
    camera.projectionMatrix,
  );
  _clipToWorldVector.multiplyScalar(1.0 / _clipToWorldVector.w);
  _clipToWorldVector.x = _lineWidth / resolution.width;
  _clipToWorldVector.y = _lineWidth / resolution.height;
  _clipToWorldVector.applyMatrix4(camera.projectionMatrixInverse);
  _clipToWorldVector.multiplyScalar(1.0 / _clipToWorldVector.w);

  return Math.abs(Math.max(_clipToWorldVector.x, _clipToWorldVector.y));
}

function raycastWorldUnits(
  lineSegments: LineSegments2,
  intersects: THREE.Intersection[],
): void {
  const matrixWorld = lineSegments.matrixWorld;
  const geometry = lineSegments.geometry as LineSegmentsGeometry;
  const instanceStart = geometry.attributes[
    "instanceStart"
  ] as THREE.InterleavedBufferAttribute;
  const instanceEnd = geometry.attributes[
    "instanceEnd"
  ] as THREE.InterleavedBufferAttribute;
  const segmentCount = Math.min(geometry.instanceCount, instanceStart.count);

  for (let i = 0, l = segmentCount; i < l; i++) {
    _line.start.fromBufferAttribute(instanceStart, i);
    _line.end.fromBufferAttribute(instanceEnd, i);

    _line.applyMatrix4(matrixWorld);

    const pointOnLine = new THREE.Vector3();
    const point = new THREE.Vector3();

    _ray.distanceSqToSegment(_line.start, _line.end, point, pointOnLine);
    const isInside = point.distanceTo(pointOnLine) < _lineWidth * 0.5;

    if (isInside) {
      intersects.push({
        point,
        pointOnLine,
        distance: _ray.origin.distanceTo(point),
        object: lineSegments,
        face: null,
        faceIndex: i,
        uv: undefined,
        uv1: undefined,
      } as unknown as THREE.Intersection);
    }
  }
}

function raycastScreenSpace(
  lineSegments: LineSegments2,
  camera: THREE.Camera,
  intersects: THREE.Intersection[],
): void {
  const projectionMatrix = camera.projectionMatrix;
  const material = lineSegments.material as LineMaterial;
  const resolution = material.resolution;
  const matrixWorld = lineSegments.matrixWorld;

  const geometry = lineSegments.geometry as LineSegmentsGeometry;
  const instanceStart = geometry.attributes[
    "instanceStart"
  ] as THREE.InterleavedBufferAttribute;
  const instanceEnd = geometry.attributes[
    "instanceEnd"
  ] as THREE.InterleavedBufferAttribute;
  const segmentCount = Math.min(geometry.instanceCount, instanceStart.count);

  const near = -(camera as THREE.PerspectiveCamera).near;

  //

  // pick a point 1 unit out along the ray to avoid the ray origin
  // sitting at the camera origin which will cause "w" to be 0 when
  // applying the projection matrix.
  _ray.at(1, _ssOrigin as unknown as THREE.Vector3);

  // ndc space [ - 1.0, 1.0 ]
  _ssOrigin.w = 1;
  _ssOrigin.applyMatrix4(camera.matrixWorldInverse);
  _ssOrigin.applyMatrix4(projectionMatrix);
  _ssOrigin.multiplyScalar(1 / _ssOrigin.w);

  // screen space
  _ssOrigin.x *= resolution.x / 2;
  _ssOrigin.y *= resolution.y / 2;
  _ssOrigin.z = 0;

  _ssOrigin3.copy(_ssOrigin as unknown as THREE.Vector3);

  _mvMatrix.multiplyMatrices(camera.matrixWorldInverse, matrixWorld);

  for (let i = 0, l = segmentCount; i < l; i++) {
    // Three.js runtime accepts InterleavedBufferAttribute here even though the
    // current TypeScript definitions for Vector4.fromBufferAttribute narrow the
    // parameter to BufferAttribute.
    _start4.fromBufferAttribute(
      instanceStart as unknown as THREE.BufferAttribute,
      i,
    );
    _end4.fromBufferAttribute(
      instanceEnd as unknown as THREE.BufferAttribute,
      i,
    );

    _start4.w = 1;
    _end4.w = 1;

    // camera space
    _start4.applyMatrix4(_mvMatrix);
    _end4.applyMatrix4(_mvMatrix);

    // skip the segment if it's entirely behind the camera
    const isBehindCameraNear = _start4.z > near && _end4.z > near;
    if (isBehindCameraNear) {
      continue;
    }

    // trim the segment if it extends behind camera near
    if (_start4.z > near) {
      const deltaDist = _start4.z - _end4.z;
      const t = (_start4.z - near) / deltaDist;
      _start4.lerp(_end4, t);
    } else if (_end4.z > near) {
      const deltaDist = _end4.z - _start4.z;
      const t = (_end4.z - near) / deltaDist;
      _end4.lerp(_start4, t);
    }

    // clip space
    _start4.applyMatrix4(projectionMatrix);
    _end4.applyMatrix4(projectionMatrix);

    // ndc space [ - 1.0, 1.0 ]
    _start4.multiplyScalar(1 / _start4.w);
    _end4.multiplyScalar(1 / _end4.w);

    // screen space
    _start4.x *= resolution.x / 2;
    _start4.y *= resolution.y / 2;

    _end4.x *= resolution.x / 2;
    _end4.y *= resolution.y / 2;

    // create 2d segment
    _line.start.copy(_start4 as unknown as THREE.Vector3);
    _line.start.z = 0;

    _line.end.copy(_end4 as unknown as THREE.Vector3);
    _line.end.z = 0;

    // get closest point on ray to segment
    const param = _line.closestPointToPointParameter(_ssOrigin3, true);
    _line.at(param, _closestPoint);

    // check if the intersection point is within clip space
    const zPos = THREE.MathUtils.lerp(_start4.z, _end4.z, param);
    const isInClipSpace = zPos >= -1 && zPos <= 1;

    const isInside =
      _ssOrigin3.distanceTo(_closestPoint) < _lineWidth * 0.5;

    if (isInClipSpace && isInside) {
      _line.start.fromBufferAttribute(instanceStart, i);
      _line.end.fromBufferAttribute(instanceEnd, i);

      _line.start.applyMatrix4(matrixWorld);
      _line.end.applyMatrix4(matrixWorld);

      const pointOnLine = new THREE.Vector3();
      const point = new THREE.Vector3();

      _ray.distanceSqToSegment(_line.start, _line.end, point, pointOnLine);

      intersects.push({
        point: point,
        pointOnLine,
        distance: _ray.origin.distanceTo(point),
        object: lineSegments,
        face: null,
        faceIndex: i,
        uv: undefined,
        uv1: undefined,
      } as unknown as THREE.Intersection);
    }
  }
}

/**
 * A series of lines drawn between pairs of vertices.
 *
 * This adds functionality beyond {@link THREE.LineSegments}, like arbitrary line width
 * and changing width to be in world units. `Line2` (separate addon) extends this
 * object, forming a polyline instead of individual segments.
 *
 * WebGLRenderer only.
 */
export class LineSegments2 extends THREE.Mesh<
  LineSegmentsGeometry,
  LineMaterial
> {
  /**
   * This flag can be used for type testing.
   */
  public readonly isLineSegments2: true = true;

  public override type: string = "LineSegments2";

  /**
   * Constructs a new wide line.
   */
  constructor(
    geometry: LineSegmentsGeometry = new LineSegmentsGeometry(),
    material: LineMaterial = new LineMaterial({
      color: Math.random() * 0xffffff,
    }),
  ) {
    super(geometry, material);
  }

  /**
   * Computes an array of distance values which are necessary for rendering dashed lines.
   * For each vertex in the geometry, the method calculates the cumulative length from the
   * current point to the very beginning of the line.
   */
  computeLineDistances(): this {
    // for backwards-compatibility, but could be a method of LineSegmentsGeometry...

    const geometry = this.geometry;

    const instanceStart = geometry.attributes[
      "instanceStart"
    ] as THREE.InterleavedBufferAttribute;
    const instanceEnd = geometry.attributes[
      "instanceEnd"
    ] as THREE.InterleavedBufferAttribute;
    const lineDistances = new Float32Array(2 * instanceStart.count);

    for (let i = 0, j = 0, l = instanceStart.count; i < l; i++, j += 2) {
      _start.fromBufferAttribute(instanceStart, i);
      _end.fromBufferAttribute(instanceEnd, i);

      lineDistances[j] = j === 0 ? 0 : lineDistances[j - 1]!;
      lineDistances[j + 1] = lineDistances[j]! + _start.distanceTo(_end);
    }

    const instanceDistanceBuffer = new THREE.InstancedInterleavedBuffer(
      lineDistances,
      2,
      1,
    ); // d0, d1

    geometry.setAttribute(
      "instanceDistanceStart",
      new THREE.InterleavedBufferAttribute(instanceDistanceBuffer, 1, 0),
    ); // d0
    geometry.setAttribute(
      "instanceDistanceEnd",
      new THREE.InterleavedBufferAttribute(instanceDistanceBuffer, 1, 1),
    ); // d1

    return this;
  }

  /**
   * Computes intersection points between a casted ray and this instance.
   */
  override raycast(
    raycaster: THREE.Raycaster,
    intersects: THREE.Intersection[],
  ): void {
    const worldUnits = this.material.worldUnits;
    const camera = raycaster.camera;

    if (camera === null && !worldUnits) {
      console.error(
        'LineSegments2: "Raycaster.camera" needs to be set in order to raycast against LineSegments2 while worldUnits is set to false.',
      );
    }

    const lineParams = (raycaster.params as { Line2?: { threshold?: number } })
      .Line2;
    const threshold =
      lineParams !== undefined ? lineParams.threshold ?? 0 : 0;

    _ray = raycaster.ray;

    const matrixWorld = this.matrixWorld;
    const geometry = this.geometry;
    const material = this.material;

    _lineWidth = material.linewidth + threshold;

    // check if we intersect the sphere bounds
    if (geometry.boundingSphere === null) {
      geometry.computeBoundingSphere();
    }

    _sphere.copy(geometry.boundingSphere!).applyMatrix4(matrixWorld);

    // increase the sphere bounds by the worst case line screen space width
    let sphereMargin: number;
    if (worldUnits) {
      sphereMargin = _lineWidth * 0.5;
    } else {
      const perspectiveCamera = camera as THREE.PerspectiveCamera;
      const distanceToSphere = Math.max(
        perspectiveCamera.near,
        _sphere.distanceToPoint(_ray.origin),
      );
      sphereMargin = getWorldSpaceHalfWidth(
        perspectiveCamera,
        distanceToSphere,
        material.resolution,
      );
    }

    _sphere.radius += sphereMargin;

    if (_ray.intersectsSphere(_sphere) === false) {
      return;
    }

    // check if we intersect the box bounds
    if (geometry.boundingBox === null) {
      geometry.computeBoundingBox();
    }

    _box.copy(geometry.boundingBox!).applyMatrix4(matrixWorld);

    // increase the box bounds by the worst case line width
    let boxMargin: number;
    if (worldUnits) {
      boxMargin = _lineWidth * 0.5;
    } else {
      const perspectiveCamera = camera as THREE.PerspectiveCamera;
      const distanceToBox = Math.max(
        perspectiveCamera.near,
        _box.distanceToPoint(_ray.origin),
      );
      boxMargin = getWorldSpaceHalfWidth(
        perspectiveCamera,
        distanceToBox,
        material.resolution,
      );
    }

    _box.expandByScalar(boxMargin);

    if (_ray.intersectsBox(_box) === false) {
      return;
    }

    if (worldUnits) {
      raycastWorldUnits(this, intersects);
    } else {
      raycastScreenSpace(this, camera as THREE.Camera, intersects);
    }
  }

  override onBeforeRender(renderer: THREE.WebGLRenderer): void {
    const uniforms = this.material.uniforms;

    if (uniforms && uniforms["resolution"]) {
      renderer.getViewport(_viewport);
      (uniforms["resolution"].value as THREE.Vector2).set(
        _viewport.z,
        _viewport.w,
      );
    }
  }
}
